import BoldSmallText from '@/components/BoldSmallText';
import CustomCard from '@/components/customCard';
import SingleDatePicker from '@/components/DatePicker/SingleDatePicker';
import RegularText from '@/components/RegularText';
import TimePicker from '@/components/TimePicker';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { DisplayModeEnum } from '@/constants/enum';
import { MAP_RULE_DISPLAY_TIME_LABEL } from '@/constants/label';
import { compareTime, createDateByTimezoneOffset, formatDate } from '@/helpers/time';
import useUserPlan from '@/hooks/useUserPlan';
import ruleSlice, { ruleErrorsSelector } from '@/redux/slice/rule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IDaySchedule } from '@/types/newSetup.type';
import { Button, Checkbox, ChoiceList, DataTable, InlineError, Tooltip, useMediaQuery } from '@shopify/polaris';
import { DuplicateIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RuleDisplayTimeStyled } from './styled';

interface IData {
  displayMode?: DisplayModeEnum;
  startDisplayDate?: string;
  endDisplayDate?: string;
  daySchedules?: IDaySchedule[];
}

interface IProps {
  data: IData;
  onChange: (data: IData) => void;
}

const RuleDisplayTime = ({ data, onChange }: IProps) => {
  const dispatch = useDispatch();
  const { isPlanLimited } = useUserPlan();
  const ruleErrors = useSelector(ruleErrorsSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const now = useMemo(() => {
    return createDateByTimezoneOffset(dataSettings?.date_timezone_offset);
  }, [dataSettings?.date_timezone_offset]);

  const { startDisplayDate, endDisplayDate } = useMemo(() => {
    return {
      startDisplayDate: formatDate(data.startDisplayDate ?? now, 'YYYY-MM-DD HH:mm'),
      endDisplayDate: formatDate(data.endDisplayDate ?? now, 'YYYY-MM-DD HH:mm'),
    };
  }, [data.endDisplayDate, data.startDisplayDate, now]);

  // specific days and time of the week
  const renderTimeSelect = useCallback(
    (input: {
      value: string;
      onChangeHour: (value: string) => void;
      onChangeMinute: (value: string) => void;
      disabled?: boolean;
    }) => {
      const { value, onChangeHour, onChangeMinute, disabled } = input;
      const [hour, minute] = value.split(':');
      return (
        <TimePicker
          gap={'200'}
          hour={hour}
          minute={minute}
          setHour={onChangeHour}
          setMinute={onChangeMinute}
          disabled={disabled}
        />
      );
    },
    [],
  );

  const handleCopyTime = useCallback(
    (currentDaySchedule: IDaySchedule | undefined) => {
      if (!currentDaySchedule) return;
      onChange({
        ...data,
        daySchedules: data.daySchedules?.map((item) => {
          return { ...item, startTime: currentDaySchedule.startTime, endTime: currentDaySchedule.endTime };
        }),
      });
    },
    [data, onChange],
  );

  const isMobile = useMediaQuery('(max-width: 600px)');

  const renderHeadings = useMemo(() => {
    return [
      <BoldSmallText key={'day'} fontWeight="medium">
        Day
      </BoldSmallText>,
      ...(isMobile
        ? [
            <div key={'start-end-time'} className="d-flex flex-column gap-4">
              <BoldSmallText fontWeight="medium">Start time (hh:mm)</BoldSmallText>
              <BoldSmallText fontWeight="medium">End time (hh:mm)</BoldSmallText>
            </div>,
          ]
        : [
            <BoldSmallText key={'start-time'} fontWeight="medium">
              Start time (hh:mm)
            </BoldSmallText>,
            <BoldSmallText key={'end-time'} fontWeight="medium">
              End time (hh:mm)
            </BoldSmallText>,
          ]),
      '',
    ];
  }, [isMobile]);

  const renderRows = useMemo(() => {
    const dayInWeek = isMobile
      ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const result = dayInWeek.map((item, index) => {
      const currentDaySchedule = data.daySchedules?.find((item) => item.weekday === index);
      const handleChangeTime = (value: string, key: 'startTime' | 'endTime', type: 'hour' | 'minute') => {
        if (isPlanLimited) return;
        onChange({
          ...data,
          daySchedules: data.daySchedules?.map((item) => {
            if (currentDaySchedule?.weekday === item.weekday) {
              const [hour, minute] = currentDaySchedule[key].split(':');
              const newValue = `${type === 'hour' ? value : hour}:${type === 'minute' ? value : minute}`;
              return { ...item, [key]: newValue };
            }
            return item;
          }),
        });
      };
      return [
        <div key={item} className="d-flex align-center">
          <Checkbox
            key={index}
            label={item}
            checked={currentDaySchedule?.enable || false}
            onChange={(value) => {
              if (isPlanLimited) return;
              onChange({
                ...data,
                daySchedules: data.daySchedules?.map((item) => ({
                  ...item,
                  enable: currentDaySchedule?.weekday === item.weekday ? value : item?.enable,
                })),
              });
            }}
          />
        </div>,
        ...(isMobile
          ? [
              <div key={`start-end-time`} className="d-flex flex-column gap-8">
                {renderTimeSelect({
                  value: currentDaySchedule?.startTime ?? '23:59',
                  onChangeHour: (value) => handleChangeTime(value, 'startTime', 'hour'),
                  onChangeMinute: (value) => handleChangeTime(value, 'startTime', 'minute'),
                })}
                {renderTimeSelect({
                  value: currentDaySchedule?.endTime ?? '23:59',
                  onChangeHour: (value) => handleChangeTime(value, 'endTime', 'hour'),
                  onChangeMinute: (value) => handleChangeTime(value, 'endTime', 'minute'),
                })}
              </div>,
            ]
          : [
              renderTimeSelect({
                value: currentDaySchedule?.startTime ?? '23:59',
                onChangeHour: (value) => handleChangeTime(value, 'startTime', 'hour'),
                onChangeMinute: (value) => handleChangeTime(value, 'startTime', 'minute'),
              }),
              renderTimeSelect({
                value: currentDaySchedule?.endTime ?? '23:59',
                onChangeHour: (value) => handleChangeTime(value, 'endTime', 'hour'),
                onChangeMinute: (value) => handleChangeTime(value, 'endTime', 'minute'),
              }),
            ]),
        index === 0 ? (
          <div className="d-flex align-center content-center">
            <Tooltip content="Copy time to all">
              <Button icon={DuplicateIcon} variant="monochromePlain" onClick={() => handleCopyTime(currentDaySchedule)} />
            </Tooltip>
          </div>
        ) : null,
      ];
    });
    return result;
  }, [data, handleCopyTime, isMobile, isPlanLimited, onChange, renderTimeSelect]);

  // specific date and time range
  const renderDateTimeSelect = useCallback(
    (input: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) => {
      // ex value: 2023-08-06 14:00
      const { label, value, onChange, disabled } = input;
      const [date, time] = value.split(' ');
      const [hour, minute] = time.split(':');
      return (
        <div className="date-time-select">
          <div style={{ minWidth: '32px' }}>{label}</div>
          <div className="date-time-select__container">
            <SingleDatePicker
              date={new Date(date)}
              setDate={(newDate) => {
                onChange(`${formatDate(newDate, 'YYYY-MM-DD')} ${hour}:${minute}`);
              }}
              disabledActivator={disabled}
            />
            at
            {renderTimeSelect({
              value: time,
              onChangeHour: (value) => onChange(`${date} ${value}:${minute}`),
              onChangeMinute: (value) => onChange(`${date} ${hour}:${value}`),
              disabled,
            })}
          </div>
        </div>
      );
    },
    [renderTimeSelect],
  );

  useEffect(() => {
    const isHaveDayScheduleError = data.daySchedules?.some((item) => {
      return compareTime(item.startTime, item.endTime) === 1;
    });
    let isHaveDateScheduleError = false;

    try {
      const startDate = new Date(startDisplayDate).getTime();
      const endDate = new Date(endDisplayDate).getTime();
      isHaveDateScheduleError = startDate > endDate;
    } catch {}

    dispatch(
      ruleSlice.actions.handleSetError({
        value: {
          field: 'daySchedules',
          msg:
            data.displayMode === DisplayModeEnum.SpecificDays
              ? isHaveDayScheduleError
                ? 'Start time must be earlier than end time'
                : ''
              : '',
        },
      }),
    );
    dispatch(
      ruleSlice.actions.handleSetError({
        value: {
          field: 'dateSchedules',
          msg:
            data.displayMode === DisplayModeEnum.DateRange
              ? isHaveDateScheduleError
                ? 'End date must be after start date'
                : ''
              : '',
        },
      }),
    );
  }, [data.daySchedules, data.displayMode, endDisplayDate, startDisplayDate, dispatch]);

  return (
    <RuleDisplayTimeStyled>
      <CustomCard title={'Rule display time'}>
        <div className="mt-8 mb-8">
          <RegularText>
            Choose when this rule should appear on your storefront. Rule outside the selected time range won't be shown. This
            setting doesn't affect estimated delivery dates.
          </RegularText>
        </div>
        <ChoiceList
          title=""
          selected={[data.displayMode || DisplayModeEnum.AllTime]}
          choices={[
            {
              label: MAP_RULE_DISPLAY_TIME_LABEL[DisplayModeEnum.AllTime],
              value: DisplayModeEnum.AllTime,
              helpText: 'The rule is always visible on your storefront.',
            },
            {
              label: (
                <div className="d-flex justify-between gap-8">
                  <RegularText>{MAP_RULE_DISPLAY_TIME_LABEL[DisplayModeEnum.SpecificDays]}</RegularText>
                  <UpgradeTextWithTouchPoint text="Pro" isVisible={isPlanLimited} />
                </div>
              ),
              helpText: 'Eg. 9:00 - 17:00 on Sunday, Monday and Thursday every week',
              value: DisplayModeEnum.SpecificDays,
              disabled: isPlanLimited,
              renderChildren: (isSelected) => {
                const daySchedulesError = ruleErrors.find((item) => item.field === 'daySchedules');

                return isSelected ? (
                  <div className={`d-flex flex-column gap-8 mb-8 ${isPlanLimited ? 'disable-content' : ''}`}>
                    <CustomCard padding={'0'}>
                      <DataTable
                        columnContentTypes={['text', 'text', 'text', 'text']}
                        headings={renderHeadings}
                        verticalAlign="middle"
                        rows={renderRows}
                      />
                    </CustomCard>
                    {daySchedulesError ? <InlineError fieldID="" message={daySchedulesError.msg} /> : null}
                  </div>
                ) : null;
              },
            },
            {
              label: (
                <div className="d-flex justify-between gap-8">
                  <RegularText>{MAP_RULE_DISPLAY_TIME_LABEL[DisplayModeEnum.DateRange]}</RegularText>
                  <UpgradeTextWithTouchPoint text="Pro" isVisible={isPlanLimited} />
                </div>
              ),
              helpText: 'Eg. From 14:00, Aug 6 to 10:00, Aug 8',
              value: DisplayModeEnum.DateRange,
              disabled: isPlanLimited,
              renderChildren: (isSelected) => {
                const dateSchedulesError = ruleErrors.find((item) => item.field === 'dateSchedules');

                return isSelected ? (
                  <div className={`d-flex flex-column gap-12 ${isPlanLimited ? 'disable-content' : ''}`}>
                    {renderDateTimeSelect({
                      label: 'From',
                      value: startDisplayDate || '',
                      onChange: (value) => {
                        if (isPlanLimited) return;
                        onChange({ ...data, startDisplayDate: value });
                      },
                      disabled: isPlanLimited,
                    })}
                    {renderDateTimeSelect({
                      label: 'To',
                      value: endDisplayDate || '',
                      onChange: (value) => {
                        if (isPlanLimited) return;
                        onChange({ ...data, endDisplayDate: value });
                      },
                      disabled: isPlanLimited,
                    })}
                    {dateSchedulesError ? <InlineError fieldID="" message={dateSchedulesError.msg} /> : null}
                  </div>
                ) : null;
              },
            },
          ]}
          onChange={(selected) => {
            onChange({
              ...data,
              displayMode: selected[0] as DisplayModeEnum,
              startDisplayDate:
                selected[0] === DisplayModeEnum.DateRange && !data.startDisplayDate
                  ? formatDate(now, 'YYYY-MM-DD HH:mm')
                  : data.startDisplayDate,
              endDisplayDate:
                selected[0] === DisplayModeEnum.DateRange && !data.endDisplayDate
                  ? formatDate(now, 'YYYY-MM-DD HH:mm')
                  : data.endDisplayDate,
            });
          }}
        />
      </CustomCard>
    </RuleDisplayTimeStyled>
  );
};

export default RuleDisplayTime;
